import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSpinner, faCheckCircle, faExclamationTriangle, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { Site } from '@/types';

const SitooAuthentication: React.FC = () => {
  const [formData, setFormData] = useState({
    site_id: '',
    api_id: '',
    password: '',
    base_url: '',
    image_url: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [alerts, setAlerts] = useState({
    saveSuccess: false,
    deleteSuccess: false,
    error: false,
  });

  const queryClient = useQueryClient();

  // Fetch sites
  const { data: sites = [] } = useQuery('sites', apiService.getSites);
  const siteId = sites.length > 0 ? sites[0].id : null;

  // Fetch Sitoo auth settings
  const { data: authSettings, isLoading } = useQuery(
    ['sitooAuth', siteId],
    () => apiService.getSitooAuthentication(siteId!),
    {
      enabled: !!siteId,
      onSuccess: (data) => {
        if (data) {
          setFormData({
            site_id: data.site_id?.toString() || '',
            api_id: data.api_id || '',
            password: data.password || '',
            base_url: data.base_url || '',
            image_url: data.image_url || '',
          });
        }
      },
    }
  );

  // Save mutation
  const saveMutation = useMutation(
    (data: any) => {
      if (authSettings?.id) {
        return apiService.updateSitooAuthentication(siteId!, data);
      } else {
        return apiService.createSitooAuthentication(siteId!, data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sitooAuth', siteId]);
        showAlert('saveSuccess');
      },
      onError: (error) => {
        console.error('Save failed:', error);
        showAlert('error');
      },
    }
  );

  const showAlert = (type: 'saveSuccess' | 'deleteSuccess' | 'error') => {
    setAlerts(prev => ({
      ...prev,
      [type]: true,
    }));

    setTimeout(() => {
      setAlerts(prev => ({
        ...prev,
        [type]: false,
      }));
    }, 3000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      site_id: parseInt(formData.site_id),
    };

    saveMutation.mutate(submitData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">Sitoo Authentication Settings</h1>
      </div>

      {/* Alerts */}
      {alerts.saveSuccess && (
        <div className="alert alert-success flex items-center space-x-2">
          <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4" />
          <span>Sitoo Authentication Settings saved</span>
        </div>
      )}

      {alerts.deleteSuccess && (
        <div className="alert alert-success flex items-center space-x-2">
          <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4" />
          <span>Sitoo Authentication Settings deleted</span>
        </div>
      )}

      {alerts.error && (
        <div className="alert alert-danger flex items-center space-x-2">
          <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4" />
          <span>Something went wrong</span>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit} className="form-section">
          <div className="form-row">
            <div className="form-column">
              <div>
                <label className="form-label form-label-required">Site:</label>
                <select
                  value={formData.site_id}
                  onChange={(e) => handleInputChange('site_id', e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="">Select Site</option>
                  {sites.map((site: Site) => (
                    <option key={site.id} value={site.id}>
                      {site.url}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label form-label-required">API ID:</label>
                <input
                  type="text"
                  value={formData.api_id}
                  onChange={(e) => handleInputChange('api_id', e.target.value)}
                  className="form-input"
                  required
                  placeholder="Enter Sitoo API ID"
                />
              </div>

              <div>
                <label className="form-label form-label-required">Password:</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="form-input pr-10"
                    required
                    placeholder="Enter Sitoo password"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FontAwesomeIcon
                      icon={showPassword ? faEyeSlash : faEye}
                      className="w-4 h-4"
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="form-column">
              <div>
                <label className="form-label form-label-required">Base URL:</label>
                <input
                  type="url"
                  value={formData.base_url}
                  onChange={(e) => handleInputChange('base_url', e.target.value)}
                  className="form-input"
                  required
                  placeholder="https://api.sitoo.com"
                />
              </div>

              <div>
                <label className="form-label form-label-required">Image URL:</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => handleInputChange('image_url', e.target.value)}
                  className="form-input"
                  required
                  placeholder="https://images.sitoo.com"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
            >
              {saveMutation.isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} className="w-4 h-4" />
                  <span>Save</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SitooAuthentication;

